if (!customElements.get('media-gallery')) {
  class MediaGallery extends HTMLElement {
    constructor() {
      super();

      if (Shopify.designMode) {
        setTimeout(() => this.init(), 200);
      } else {
        this.init();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('on:debounced-resize', this.resizeHandler);
      if (this.viewer && this.scrollHandler) {
        this.viewer.removeEventListener('scroll', this.scrollHandler);
      }

      if (this.controls && this.navClickHandler) {
        this.controls.removeEventListener('click', this.navClickHandler);
      }

      if (this.thumbs && this.thumbClickHandler) {
        this.thumbs.removeEventListener('click', this.thumbClickHandler);
      }

      if (this.galleryInitRetry) {
        clearTimeout(this.galleryInitRetry);
      }

      if (this.resizeInitHandler) {
        window.removeEventListener('on:debounced-resize', this.resizeInitHandler);
      }

      if (this.zoomInitHandler) {
        window.removeEventListener('on:debounced-resize', this.zoomInitHandler);
      }
    }

    init() {
      this.section = this.closest('.js-product');
      this.mediaGroupingEnabled = this.hasAttribute('data-media-grouping-enabled')
        && this.getMediaGroupData();
      this.stackedScroll = this.dataset.stackedScroll;
      this.stackedUnderline = this.dataset.stackedUnderline === 'true' && !this.mediaGroupingEnabled;
      this.isFeatured = this.dataset.isFeatured === 'true';
      this.viewer = this.querySelector('.media-viewer');
      this.thumbs = this.querySelector('.media-thumbs');
      this.thumbsItems = this.querySelectorAll('.media-thumbs__item');
      this.controls = this.querySelector('.media-ctrl');
      this.prevBtn = this.querySelector('.media-ctrl__btn[name="prev"]');
      this.nextBtn = this.querySelector('.media-ctrl__btn[name="next"]');
      this.counterCurrent = this.querySelector('.media-ctrl__current-item');
      this.counterTotal = this.querySelector('.media-ctrl__total-items');
      this.liveRegion = this.querySelector('.media-gallery__status');
      this.zoomLinks = this.querySelectorAll('.js-zoom-link');
      this.loadingSpinner = this.querySelector('.loading-spinner');
      this.xrButton = this.querySelector('.media-xr-button');

      if (this.mediaGroupingEnabled) {
        this.setActiveMediaGroup(this.getMediaGroupName());
      }

      if (this.dataset.layout === 'stacked' && theme.mediaMatches.md) {
        this.resizeInitHandler = this.resizeInitHandler || this.initGallery.bind(this);
        window.addEventListener('on:debounced-resize', this.resizeInitHandler);
        this.setVisibleItems();
        this.previousMediaItem = this.querySelector('.media-viewer__item.is-current-variant');
        setTimeout(() => this.customSetActiveMedia(this.previousMediaItem, this.stackedScroll === 'always'), 200);
      } else {
        this.initGallery();
      }

      if (this.zoomLinks) {
        this.zoomInitHandler = this.zoomInitHandler || this.initZoom.bind(this);
        this.zoomEventListener = this.zoomEventListener || this.handleZoomMouseMove.bind(this);
        window.addEventListener('on:debounced-resize', this.zoomInitHandler);
        this.initZoom();
        this.zoomLinks.forEach((el) => {
          el.addEventListener('click', (evt) => {
            evt.preventDefault();
          });
        });
      }

      this.section.addEventListener('on:variant:change', this.onVariantChange.bind(this));
    }

    /**
     * Handle a change in variant on the page.
     * @param {Event} evt - variant change event dispatched by variant-picker
     */
    onVariantChange(evt) {
      if (this.mediaGroupingEnabled) {
        this.setActiveMediaGroup(this.getMediaGroupName(evt.detail.variant));
      }

      if (evt.detail.variant && evt.detail.variant.featured_media) {
        const variantMedia = this.viewer.querySelector(
          `[data-media-id="${evt.detail.variant.featured_media.id}"]`
        );
        this.customSetActiveMedia(variantMedia, true);
      }
    }

    /**
     * Gets the media group name from the current grouping mode.
     * @param {?object} variant - Current variant from variant-picker.
     * @returns {?string}
     */
    getMediaGroupName(variant = null) {
      const mediaGroupData = this.getMediaGroupData();

      if (mediaGroupData.groupMode === 'variant') {
        if (variant && variant.id) {
          return String(variant.id);
        }

        return mediaGroupData.selectedVariantId
          ? String(mediaGroupData.selectedVariantId)
          : null;
      }

      return this.getMediaGroupFromOptionSelectors();
    }

    /**
     * Gets the media group from currently selected variant options.
     * @returns {?object}
     */
    getMediaGroupFromOptionSelectors() {
      const optionSelectors = this.section.querySelectorAll('.option-selector');
      if (optionSelectors.length > this.getMediaGroupData().groupOptionIndex) {
        const selector = optionSelectors[this.getMediaGroupData().groupOptionIndex];
        if (selector.dataset.selectorType === 'dropdown') {
          return selector.querySelector('.custom-select__btn').textContent.trim();
        }
        return selector.querySelector('input:checked').value;
      }
      return null;
    }

    /**
     * Gets the variant media associations for a product.
     * @returns {?object}
     */
    getMediaGroupData() {
      if (typeof this.variantMediaData === 'undefined') {
        const dataEl = this.querySelector('.js-data-variant-media');
        this.variantMediaData = dataEl ? JSON.parse(dataEl.textContent) : false;
      }

      return this.variantMediaData;
    }

    /**
     * Gets an object mapping media to groups, and the reverse
     * @returns {?object}
     */
    getMediaGroupMap() {
      if (!this.mediaGroupMap) {
        this.mediaGroupMap = {
          groups: {}
        };

        // set up grouping
        const variantMediaData = this.getMediaGroupData();
        let currentMediaGroupName = false;
        this.viewerItems = this.querySelectorAll('.media-viewer__item');
        this.viewerItems.forEach((item) => {
          for (let i = 0; i < variantMediaData.variantMedia.length; i += 1) {
            if (parseInt(item.dataset.mediaId, 10) === variantMediaData.variantMedia[i].mediaId) {
              const nextMediaGroupName = variantMediaData.variantMedia[i].groupKey
                || variantMediaData.variantMedia[i].option;

              if (currentMediaGroupName !== nextMediaGroupName) {
                currentMediaGroupName = String(nextMediaGroupName);
              }
            }
          }
          if (currentMediaGroupName) {
            if (!this.mediaGroupMap.groups[currentMediaGroupName]) {
              this.mediaGroupMap.groups[currentMediaGroupName] = {
                name: currentMediaGroupName,
                items: []
              };
            }
            const groupItem = { main: item };
            if (this.thumbs) {
              groupItem.thumb = this.thumbs.querySelector(
                `[data-media-id="${item.dataset.mediaId}"].media-thumbs__item`
              );
            }
            this.mediaGroupMap.groups[currentMediaGroupName].items.push(groupItem);
          }
        });

        // add helper
        this.mediaGroupMap.groupFromItem = (item) => {
          const groups = Object.keys(this.mediaGroupMap.groups);
          for (let i = 0; i < groups.length; i += 1) {
            const group = groups[i];
            for (let j = 0; j < this.mediaGroupMap.groups[group].items.length; j += 1) {
              if (this.mediaGroupMap.groups[group].items[j].main === item) {
                return this.mediaGroupMap.groups[group];
              }
            }
          }
          return this.mediaGroupMap.groups[Object.keys(this.mediaGroupMap.groups)[0]];
        };
      }

      return this.mediaGroupMap;
    }

    /**
     * Show only images associated to the current variant
     * @param {string} groupName - optional - Group to show (uses this.currentItem if empty)
     */
    setActiveMediaGroup(groupName) {
      const mediaGroupMap = this.getMediaGroupMap();
      const selectedGroup = groupName
        ? mediaGroupMap.groups[groupName]
        : mediaGroupMap.groupFromItem(this.currentItem);

      if (selectedGroup) {
        if (this.currentGroup !== selectedGroup) {
          this.currentGroup = selectedGroup;
          this.viewerItems.forEach((item) => {
            item.style.display = 'none';
            item.classList.remove('media-viewer__item--single');
          });
          this.thumbsItems.forEach((item) => {
            item.style.display = 'none';
          });

          let currentItemIsVisible = false;
          selectedGroup.items.forEach((item) => {
            item.main.style.display = '';
            if (item.thumb) {
              item.thumb.style.display = '';
            }
            if (item.main === this.currentItem) {
              currentItemIsVisible = true;
            }
          });
          this.setVisibleItems();
          if (this.visibleItems.includes(this.currentItem)) {
            this.currentIndex = this.visibleItems.indexOf(this.currentItem);
          }
          this.updateControls();

          // If current item is not in this group, set it as the active item
          if (!currentItemIsVisible) {
            this.customSetActiveMedia(selectedGroup.items[0].main, true);
          }

          // Handle single images on stacked view
          if (selectedGroup.items.length === 1) {
            selectedGroup.items[0].main.classList.add('media-viewer__item--single');
          }
        }
      } else {
        this.currentGroup = null;
        this.viewerItems.forEach((item) => {
          item.style.display = '';
        });
        this.thumbsItems.forEach((item) => {
          item.style.display = '';
        });
        this.setVisibleItems();
        if (this.visibleItems.includes(this.currentItem)) {
          this.currentIndex = this.visibleItems.indexOf(this.currentItem);
        }
        this.updateControls();
      }
    }

    /**
     * Initialises the media gallery slider and associated controls.
     */
    initGallery() {
      this.setVisibleItems();
      this.addListeners();

      if (this.visibleItems.length <= 1) {
        this.currentItem = this.visibleItems[0] || this.querySelector('.media-viewer__item.is-current-variant');
        this.currentIndex = this.currentItem ? Math.max(this.visibleItems.indexOf(this.currentItem), 0) : 0;
        this.viewerItemOffset = 0;
        this.updateControls();
        this.scheduleGalleryInitRetry();
        return;
      }

      this.viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;
      if (!this.viewerItemOffset) {
        this.currentItem = this.visibleItems[0];
        this.currentIndex = 0;
        this.updateControls();
        this.scheduleGalleryInitRetry();
        return;
      }

      this.currentIndex = Math.round(this.viewer.scrollLeft / this.viewerItemOffset);
      this.currentIndex = Math.min(Math.max(this.currentIndex, 0), this.visibleItems.length - 1);
      this.currentItem = this.visibleItems[this.currentIndex];
      this.galleryInitRetryCount = 0;
      this.updateControls();

      if (this.thumbs && this.currentItem) {
        this.currentThumb = this.thumbs.querySelector(
          `[data-media-id="${this.currentItem.dataset.mediaId}"]`
        );
      }

      if (!this.isFeatured && document.hasFocus()) {
        // Eager load the slider images for smooth UX
        this.viewer.querySelectorAll('.product-image[loading="lazy"]').forEach((img, index) => {
          setTimeout(() => {
            img.loading = 'eager';
          }, 500 * (index + 1));
        });
      }

      const currentItem = this.querySelector('.media-viewer__item.is-current-variant');
      if (currentItem && this.visibleItems.includes(currentItem)) {
        this.customSetActiveMedia(currentItem, true);
      }
    }

    addListeners() {
      if (this.listenersAdded) return;

      this.scrollHandler = this.scrollHandler || this.handleScroll.bind(this);
      this.navClickHandler = this.navClickHandler || this.handleNavClick.bind(this);
      this.thumbClickHandler = this.thumbClickHandler || this.handleThumbClick.bind(this);
      this.resizeHandler = this.resizeHandler || this.handleResize.bind(this);

      this.viewer.addEventListener('scroll', this.scrollHandler);
      if (this.controls) this.controls.addEventListener('click', this.navClickHandler);
      if (this.thumbs) this.thumbs.addEventListener('click', this.thumbClickHandler);
      window.addEventListener('on:debounced-resize', this.resizeHandler);
      this.listenersAdded = true;
    }

    /**
     * Initialized the zoom on hover for desktop
     */
    initZoom() {
      this.zoomLinks.forEach((el) => {
        const zoomWidth = Number(el.querySelector('.zoom-image').dataset.originalWidth || 0);
        const imageWidth = el.querySelector('.product-image').getBoundingClientRect().width;
        if (theme.mediaMatches.md && ((zoomWidth - 75) > (imageWidth))) {
          el.addEventListener('mousemove', this.zoomEventListener);
          el.classList.remove('pointer-events-none');
        } else {
          el.removeEventListener('mousemove', this.zoomEventListener);
          el.classList.add('pointer-events-none');
        }
      });
    }

    /**
     * Handles mouse move over a zoomable image
     * @param {?object} evt - Event object.
     */
    handleZoomMouseMove(evt) {
      const hoverElem = evt.currentTarget;
      const zoomImage = hoverElem.querySelector('.js-zoom-image');

      // Download the zoom image if necessary
      if (zoomImage.dataset.src) {
        this.loadingSpinner.classList.remove('loading-spinner--out');

        const img = new Image();
        img.src = zoomImage.dataset.src;
        img.onload = () => {
          zoomImage.src = img.src;
          hoverElem.classList.remove('media--zoom-not-loaded');
          this.loadingSpinner.classList.add('loading-spinner--out');
        };
        zoomImage.removeAttribute('data-src');
      }

      try {
        const offsetX = evt.offsetX ? evt.offsetX : evt.touches[0].pageX;
        const offsetY = evt.offsetY ? evt.offsetY : evt.touches[0].pageY;
        const x = (offsetX / zoomImage.offsetWidth) * 100;
        const y = (offsetY / zoomImage.offsetHeight) * 100;
        zoomImage.style.objectPosition = `${x}% ${y}%`;
      } catch (err) {
        // Fail silently
      }
    }

    /**
     * Handles 'scroll' events on the main media container.
     */
    handleScroll() {
      if (!this.visibleItems || this.visibleItems.length <= 1 || !this.viewerItemOffset) return;

      const newIndex = Math.round(this.viewer.scrollLeft / this.viewerItemOffset);
      if (!this.visibleItems[newIndex]) return;

      if (newIndex !== this.currentIndex) {
        const viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;
        if (!viewerItemOffset) return;

        // If scroll wasn't caused by a resize event, update the active media.
        if (viewerItemOffset === this.viewerItemOffset) {
          this.customSetActiveMedia(this.visibleItems[newIndex], false);
        }
      }
    }

    /**
     * Handles 'click' events on the controls container.
     * @param {object} evt - Event object.
     */
    handleNavClick(evt) {
      const button = evt.target.closest('.media-ctrl__btn');
      if (!button || button.disabled || (button !== this.prevBtn && button !== this.nextBtn)) return;

      this.setVisibleItems();
      if (this.visibleItems.length <= 1) {
        this.updateControls();
        return;
      }

      if (!this.visibleItems.includes(this.currentItem)) {
        if (!this.viewerItemOffset) {
          this.viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;
        }

        this.currentIndex = this.viewerItemOffset
          ? Math.round(this.viewer.scrollLeft / this.viewerItemOffset)
          : 0;
        this.currentIndex = Math.min(Math.max(this.currentIndex, 0), this.visibleItems.length - 1);
        this.currentItem = this.visibleItems[this.currentIndex];
      }

      const itemToShow = button === this.nextBtn
        ? this.visibleItems[this.currentIndex + 1]
        : this.visibleItems[this.currentIndex - 1];

      if (!itemToShow) {
        this.updateControls();
        return;
      }

      this.customSetActiveMedia(itemToShow, true);
    }

    /**
     * Handles 'click' events on the thumbnails container.
     * @param {object} evt - Event object.
     */
    handleThumbClick(evt) {
      const thumb = evt.target.closest('[data-media-id]');
      if (!thumb) return;

      const itemToShow = this.querySelector(`[data-media-id="${thumb.dataset.mediaId}"]`);
      if (!itemToShow) return;

      this.customSetActiveMedia(itemToShow);

      MediaGallery.playActiveMedia(itemToShow);
    }

    /**
     * Handles debounced 'resize' events on the window.
     */
    handleResize() {
      this.setVisibleItems();
      if (this.visibleItems.length <= 1) {
        this.viewerItemOffset = 0;
        this.currentIndex = 0;
        this.currentItem = this.visibleItems[0] || this.currentItem;
        this.updateControls();
        this.scheduleGalleryInitRetry();
        return;
      }

      // Reset distance from leading edge of one slide to the next.
      this.viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;
      if (!this.viewerItemOffset) {
        this.updateControls();
        this.scheduleGalleryInitRetry();
        return;
      }

      this.currentIndex = Math.min(Math.max(this.visibleItems.indexOf(this.currentItem), 0), this.visibleItems.length - 1);
      this.updateControls();

      if (this.thumbs && this.currentThumb) {
        this.checkThumbVisibilty(this.currentThumb);
      }
    }

    /**
     * Stub for variant-picker calls. Listening to change event instead.
     */
    // eslint-disable-next-line class-methods-use-this
    setActiveMedia() {}

    /**
     * Sets the active media item.
     * @param {Element} mediaItem - Media element to set as active.
     * @param {boolean} [scrollToItem=true] - Scroll to the active media item.
     */
    customSetActiveMedia(mediaItem, scrollToItem = true) {
      if (!mediaItem) return;
      if (!this.visibleItems) this.setVisibleItems();
      if (!this.visibleItems.includes(mediaItem)) this.setVisibleItems();
      if (!this.visibleItems.includes(mediaItem)) return;

      if (mediaItem === this.currentItem) return;
      window.pauseAllMedia(this);
      this.currentItem = mediaItem;
      this.currentIndex = this.visibleItems.indexOf(this.currentItem);

      if (this.dataset.layout === 'stacked' && theme.mediaMatches.md) {
        // Update the active class and scroll to the active media
        if (this.stackedUnderline) {
          if (this.previousMediaItem) this.previousMediaItem.classList.remove('is-active');
          mediaItem.classList.add('is-active');
          this.previousMediaItem = mediaItem;
        }

        if (this.stackedScroll !== 'never') {
          const y = mediaItem.getBoundingClientRect().top
            + document.documentElement.scrollTop - 150;

          // If the element is far enough away to scroll to it
          if (Math.abs(y - document.documentElement.scrollTop) > 300) {
            window.scrollTo({
              top: y < 100 ? 0 : y,
              behavior: 'smooth'
            });
          }
        }
        return;
      }

      if (scrollToItem) this.viewer.scrollTo({ left: this.currentItem.offsetLeft });
      if (this.thumbs) this.setActiveThumb();

      this.updateControls();

      this.announceLiveRegion(this.currentItem, this.currentIndex + 1);

      if (this.xrButton && mediaItem.dataset.mediaType === 'model') {
        this.xrButton.dataset.shopifyModel3dId = mediaItem.dataset.mediaId;
      }
    }

    /**
     * Sets the active thumbnail.
     */
    setActiveThumb() {
      this.currentThumb = this.thumbs.querySelector(
        `[data-media-id="${this.currentItem.dataset.mediaId}"]`
      );
      if (!this.currentThumb) return;

      const btn = this.currentThumb.querySelector('button');

      this.thumbs.querySelectorAll('.media-thumbs__btn').forEach((el) => {
        el.classList.remove('is-active');
        el.removeAttribute('aria-current');
      });

      btn.classList.add('is-active');
      btn.setAttribute('aria-current', 'true');
      this.checkThumbVisibilty(this.currentThumb);
    }

    /**
     * Schedules a short retry when first-load layout measurements are not ready.
     */
    scheduleGalleryInitRetry() {
      if (this.galleryInitRetry || this.viewerItems.length <= 1) return;
      this.galleryInitRetryCount = this.galleryInitRetryCount || 0;
      if (this.galleryInitRetryCount >= 3) return;

      const retryDelays = [50, 150, 350];
      const retryDelay = retryDelays[this.galleryInitRetryCount];
      this.galleryInitRetryCount += 1;
      this.galleryInitRetry = setTimeout(() => {
        this.galleryInitRetry = null;
        window.requestAnimationFrame(() => this.initGallery());
      }, retryDelay);
    }

    /**
     * Updates navigation controls to match the currently visible media group.
     */
    updateControls() {
      if (!this.controls) return;

      const totalItems = this.visibleItems ? this.visibleItems.length : 0;
      const rawIndex = Number.isFinite(this.currentIndex) ? this.currentIndex : 0;
      const currentIndex = totalItems ? Math.min(Math.max(rawIndex, 0), totalItems - 1) : 0;

      if (this.prevBtn) {
        this.prevBtn.disabled = totalItems <= 1 || currentIndex === 0;
      }

      if (this.nextBtn) {
        this.nextBtn.disabled = totalItems <= 1 || currentIndex === totalItems - 1;
      }

      if (this.counterCurrent) {
        this.counterCurrent.textContent = totalItems ? currentIndex + 1 : 0;
      }
    }

    /**
     * Creates an array of the visible media items.
     */
    setVisibleItems() {
      this.viewerItems = this.querySelectorAll('.media-viewer__item');
      this.visibleItems = Array.from(this.viewerItems).filter((el) => el.clientWidth > 0);
      if (this.counterTotal) {
        this.counterTotal.textContent = this.visibleItems.length;
      }
    }

    /**
     * Ensures a thumbnail is in the visible area of the slider.
     * @param {Element} thumb - Thumb item element.
     */
    checkThumbVisibilty(thumb) {
      const scrollPos = this.thumbs.scrollLeft;
      const lastVisibleThumbOffset = this.thumbs.clientWidth + scrollPos;
      const thumbOffset = thumb.offsetLeft;

      if (thumbOffset + thumb.clientWidth > lastVisibleThumbOffset || thumbOffset < scrollPos) {
        this.thumbs.scrollTo({ left: thumbOffset, behavior: 'smooth' });
      }
    }

    /**
     * Updates the media gallery status.
     * @param {Element} mediaItem - Active media element.
     * @param {number} index - Active media index.
     */
    announceLiveRegion(mediaItem, index) {
      const image = mediaItem.querySelector('.media-viewer img');
      if (!image) return;

      this.liveRegion.setAttribute('aria-hidden', 'false');
      this.liveRegion.innerHTML = theme.strings.imageAvailable.replace('[index]', index);

      setTimeout(() => {
        this.liveRegion.setAttribute('aria-hidden', 'true');
      }, 2000);
    }

    /**
     * Loads the deferred media for the active item.
     * @param {Element} mediaItem - Active media element.
     */
    static playActiveMedia(mediaItem) {
      window.pauseAllMedia();
      const deferredMedia = mediaItem.querySelector('deferred-media');
      if (deferredMedia) deferredMedia.loadContent();
    }
  }

  customElements.define('media-gallery', MediaGallery);
}
